import { ChangeDetectionStrategy, Component, computed, inject, ViewEncapsulation } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { AppService } from '@src/suite/app.service';
import { ViewStateTypes } from '@src/suite/common/enums/viewstate-types';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { CreateViewStateActions } from '@src/suite/common/store/createviewstate/createviewstate.actions';
import { selectSupervisor } from '@src/suite/common/store/hello-results/hello-results.selectors';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { selectCurrentGroup, selectCurrentSection } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { selectCurrentViewState } from '@src/suite/common/store/navigation/selectors/viewstate.selectors';
import { TopicCustomFieldsComponent } from '@src/suite/playbook/components/topic-custom-fields/topic-custom-fields';
import { take } from 'rxjs';

@Component({
    selector: 'st-base-secondary-cta',
    imports: [MatButtonToggleModule, MatListModule, MatMenuModule, MatTooltipModule],
    template: '',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class BaseSecondaryCtaComponent {
    store = inject(Store);
    appService = inject(AppService);
    visualStateService = inject(VisualStateService);
    nomWebServicesJson = inject(NomWebServicesJson);
    globalTablesService = inject(GlobalTablesService);
    dialog = inject(MatDialog);

    topicCardCustomFieldsDialogRef: MatDialogRef<TopicCustomFieldsComponent, any> = null;
    viewStateTypes = ViewStateTypes;

    readonly viewState = computed(() => this.store.selectSignal(selectCurrentViewState)());
    readonly group = computed(() => this.store.selectSignal(selectCurrentGroup)());
    readonly section = computed(() => this.store.selectSignal(selectCurrentSection)());
    readonly languageSettings = this.store.selectSignal(selectI18N)();
    readonly isSupervisor = this.store.selectSignal(selectSupervisor);

    readonly saveAsViewSettingsEnabled = computed(() => {
        if (this.viewState()?.PSTViewStateType === ViewStateTypes.SEARCH) return false;
        if (this.viewState()?.Path?.isTeamView && !this.isSupervisor()) return false;
        return true;
    });

    readonly saveViewSettingsEnabled = computed(() => {
        if (this.viewState()?.Path?.isTeamView && !this.isSupervisor()) return false;
        return true;
    });

    public async openTopicCardCustomFieldsDialog() {
        this.dialog.closeAll();
        await this.dialog.afterAllClosed.pipe(take(1)).subscribe(() => {
            this.topicCardCustomFieldsDialogRef = this.dialog.open(TopicCustomFieldsComponent, {
                hasBackdrop: true,
                disableClose: false,
                panelClass: 'desktop',
                minWidth: '1202px',
                maxWidth: '100%',
            });
            this.topicCardCustomFieldsDialogRef.componentInstance.changeDetectorRef.detectChanges();
        });
    }

    public saveViewState() {
        this.store.dispatch(NavigationApiActions.saveCurrentNavigationItem());
    }

    public async saveAsViewState() {
        const group = this.group();
        const section = this.section();
        const vs = this.viewState();
        this.store.dispatch(CreateViewStateActions.createUpdateViewState({ group, section, item: null, edit: false, copy: true, vs }));
    }
}
